import { useState, useEffect } from 'react';
import { Link } from 'wouter';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Mail, Search, Plus, Edit, Trash2, Calendar, Code, Send, Loader2, FileCode, Copy, Check, Eye, RefreshCw } from 'lucide-react';
import { useAuth } from '@/contexts/use-auth';
import { useToast } from '@/hooks/use-toast';
import { getNewsletters, getNewsletter, deleteNewsletter, saveNewsletterTemplate, getNewsletterTemplate, syncKlaviyoEmails, getAppSettings, updateNewsletter, createKlaviyoTemplate, Timestamp } from '@/lib/firebase';
import { logger } from '@/lib/logger';
import { renderNewsletter, DEFAULT_NEWSLETTER_TEMPLATE, type NewsletterData } from '@/lib/newsletter-template-engine';
import { captureHandledException } from '@/lib/sentry';

interface Newsletter {
  id: string;
  emailSubject: string;
  emailSubheading: string;
  newsletterDate: Timestamp | string;
  leadArticle?: {
    title: string;
    imageUrl: string;
    text: string[];
    links: { text: string; url: string }[];
  };
  features?: any[];
  eventCalendar?: any[];
  created?: Timestamp;
  klaviyoTemplateId?: string;
}

export default function Newsletters() {
  const [newsletters, setNewsletters] = useState<Newsletter[]>([]);
  const [filteredNewsletters, setFilteredNewsletters] = useState<Newsletter[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { user } = useAuth();
  const { toast } = useToast();

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewNewsletterId, setPreviewNewsletterId] = useState('');
  const [generating, setGenerating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncResult, setSyncResult] = useState<any>(null);
  const [previewTab, setPreviewTab] = useState<'preview' | 'html'>('preview');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const resetKey = 'newsletter_template_reset_v18';
    if (!sessionStorage.getItem(resetKey)) {
      sessionStorage.setItem(resetKey, 'true');
      saveNewsletterTemplate({ html: DEFAULT_NEWSLETTER_TEMPLATE, updated: new Date() }).catch((error) => {
        captureHandledException(error, "newsletters.save-default-template");
      });
    }
  }, []);

  useEffect(() => {
    if (!user) return;

    logger.log('Loading newsletters...');
    const loadData = async () => {
      try {
        const newslettersData = await getNewsletters() as Newsletter[];
        logger.log('Newsletters loaded:', newslettersData.length);
        setNewsletters(newslettersData);
      } catch (error: any) {
        logger.error('Error loading newsletters:', error);
        toast({ title: "Failed to load newsletters", description: error.message, variant: "destructive" });
      } finally {
        setLoading(false);
      }
    };
    loadData();
    const timer = setInterval(loadData, 15_000);
    return () => clearInterval(timer);
  }, [user, toast]);

  useEffect(() => {
    let filtered = newsletters;

    if (searchTerm.trim()) {
      filtered = filtered.filter(newsletter =>
        newsletter.emailSubject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        newsletter.emailSubheading?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredNewsletters(filtered);
  }, [newsletters, searchTerm]);

  const handleDeleteNewsletter = async (newsletterId: string, subject: string) => {
    if (!confirm(`Are you sure you want to delete "${subject}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteNewsletter(newsletterId);
      toast({
        title: "Newsletter deleted",
        description: `${subject} has been deleted successfully.`,
      });
    } catch (error: any) {
      logger.error('Error deleting newsletter:', error);
      toast({
        title: "Failed to delete newsletter",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const formatDate = (date: Timestamp | string | undefined) => {
    if (!date) return 'No date';
    if (typeof date === 'string') return date;
    if (date instanceof Timestamp) {
      return date.toDate().toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    }
    return 'Invalid date';
  };

  const handleSyncKlaviyo = async () => {
    setSyncing(true);
    try {
      const result = await syncKlaviyoEmails({});
      setSyncResult(result);
      setSyncDialogOpen(true);
    } catch (err: any) {
      captureHandledException(err, "newsletters.sync-klaviyo");
      toast({ title: 'Sync failed', description: err.message || 'Failed to sync Klaviyo emails.', variant: 'destructive' });
    } finally {
      setSyncing(false);
    }
  };

  const handleGenerateHtml = async (newsletter: Newsletter) => {
    setGenerating(true);
    try {
      const [templateData, appSettingsArr] = await Promise.all([
        getNewsletterTemplate(),
        getAppSettings(),
      ]);
      const template = (templateData?.html) ? templateData.html : DEFAULT_NEWSLETTER_TEMPLATE;
      const appSettings = Array.isArray(appSettingsArr) && appSettingsArr.length > 0 ? appSettingsArr[0] : {};

      const newsletterDate = newsletter.newsletterDate instanceof Timestamp
        ? newsletter.newsletterDate.toDate().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
        : String(newsletter.newsletterDate || '');

      const data: NewsletterData = {
        emailSubject: newsletter.emailSubject || '',
        emailSubheading: newsletter.emailSubheading || '',
        newsletterDate,
        logoUrl: appSettings.logoUrl || '',
        downloadUrlAppStore: appSettings.downloadUrlAppStore || '',
        downloadUrlGooglePlay: appSettings.downloadUrlGooglePlay || '',
        leadArticle: {
          title: newsletter.leadArticle?.title || '',
          imageUrl: newsletter.leadArticle?.imageUrl || '',
          text: newsletter.leadArticle?.text || [],
          links: newsletter.leadArticle?.links || [],
        },
        features: (newsletter.features || []).map((f: any, i: number) => ({
          featureType: f.featureType || '',
          sourceId: f.sourceId || '',
          title: f.title || '',
          imageUrl: f.imageUrl || '',
          description: f.description || '',
          featureText: f.featureText || '',
          featureTag: f.featureTag || '',
          displayOrder: f.displayOrder || i + 1,
          tags: f.tags || [],
        })),
        eventCalendar: (newsletter.eventCalendar || []).map((e: any) => ({
          eventId: e.eventId || '',
          eventType: e.eventType || '',
          title: e.title || '',
          dateTime: e.dateTime || '',
          subtitle: e.subtitle || '',
          imageUrl: e.imageUrl || '',
        })),
      };

      const html = renderNewsletter(template, data);
      setPreviewHtml(html);
      setPreviewNewsletterId(newsletter.id);
      setPreviewTab('preview');
      setCopied(false);
      setPreviewOpen(true);
    } catch (error: any) {
      logger.error('Error generating HTML:', error);
      toast({
        title: 'Failed to generate HTML',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmitHtml = async () => {
    setSubmitting(true);
    try {
      const newsletter = newsletters.find(n => n.id === previewNewsletterId);
      if (!newsletter) throw new Error('Newsletter not found');

      const payload: Record<string, string> = {
        name: newsletter.emailSubject || 'Untitled Newsletter',
        html: previewHtml,
        subject: newsletter.emailSubject || '',
      };
      if (newsletter.klaviyoTemplateId) {
        payload.template_id = newsletter.klaviyoTemplateId;
      }

      const responseData = await createKlaviyoTemplate(payload) as any;

      if (responseData?.template_id) {
        await updateNewsletter(previewNewsletterId, {
          klaviyoTemplateId: responseData.template_id,
        });
      }

      toast({
        title: 'Template submitted to Klaviyo',
        description: responseData?.template_id
          ? `Template ID: ${responseData.template_id}`
          : 'The newsletter template has been submitted.',
      });
      setPreviewOpen(false);
    } catch (error: any) {
      logger.error('Error submitting newsletter to Klaviyo:', error);
      toast({
        title: 'Failed to submit template',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-center text-slate-600">Loading newsletters...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Mail className="h-6 w-6" />
              Newsletters
            </h1>
            <p className="text-slate-600 mt-1">
              Create and manage email newsletters
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="flex items-center gap-2" onClick={handleSyncKlaviyo} disabled={syncing}>
              {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Sync Klaviyo Emails
            </Button>
            <Link href="/newsletters/template">
              <Button variant="outline" className="flex items-center gap-2">
                <FileCode className="h-4 w-4" />
                Edit Template
              </Button>
            </Link>
            <Link href="/newsletters/add">
              <Button className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Add Newsletter
              </Button>
            </Link>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Search</CardTitle>
            <CardDescription>
              Find newsletters by subject or subheading
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
              <Input
                placeholder="Search newsletters..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              Newsletters ({filteredNewsletters.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredNewsletters.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                {newsletters.length === 0
                  ? "No newsletters found. Click 'Add Newsletter' to create one."
                  : "No newsletters match your search criteria."}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Subject</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Lead Article</TableHead>
                    <TableHead>Features</TableHead>
                    <TableHead>Events</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredNewsletters.map((newsletter) => (
                    <TableRow key={newsletter.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{newsletter.emailSubject || 'Untitled'}</div>
                          {newsletter.emailSubheading && (
                            <div className="text-sm text-slate-500">{newsletter.emailSubheading}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <Calendar className="h-4 w-4 text-slate-400" />
                          {formatDate(newsletter.newsletterDate)}
                        </div>
                      </TableCell>
                      <TableCell>
                        {newsletter.leadArticle?.title ? (
                          <Badge variant="secondary">{newsletter.leadArticle.title.substring(0, 20)}...</Badge>
                        ) : (
                          <span className="text-slate-400">None</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{newsletter.features?.length || 0}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{newsletter.eventCalendar?.length || 0}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleGenerateHtml(newsletter)}
                            disabled={generating}
                            title="Generate HTML"
                          >
                            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Code className="h-4 w-4" />}
                          </Button>
                          <Link href={`/newsletters/edit/${newsletter.id}`}>
                            <Button variant="outline" size="sm" title="Edit">
                              <Edit className="h-4 w-4" />
                            </Button>
                          </Link>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteNewsletter(newsletter.id, newsletter.emailSubject)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Newsletter HTML Preview</DialogTitle>
            <DialogDescription>Review the generated HTML before submitting</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 mb-2">
            <Button
              variant={previewTab === 'preview' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPreviewTab('preview')}
            >
              <Eye className="h-4 w-4 mr-1" />
              Preview
            </Button>
            <Button
              variant={previewTab === 'html' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPreviewTab('html')}
            >
              <Code className="h-4 w-4 mr-1" />
              View HTML
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                navigator.clipboard.writeText(previewHtml);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}
            >
              {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
              {copied ? 'Copied' : 'Copy HTML'}
            </Button>
          </div>
          <div className="flex-1 overflow-auto border rounded-lg bg-white">
            {previewTab === 'preview' ? (
              <iframe
                srcDoc={previewHtml}
                className="w-full min-h-[500px] border-0"
                title="Newsletter Preview"
                sandbox="allow-same-origin"
              />
            ) : (
              <pre className="p-4 text-xs font-mono whitespace-pre-wrap break-all text-slate-800 max-h-[500px] overflow-auto">{previewHtml}</pre>
            )}
          </div>
          <DialogFooter className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>
              Close
            </Button>
            <Button onClick={handleSubmitHtml} disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  {newsletters.find(n => n.id === previewNewsletterId)?.klaviyoTemplateId ? 'Update Klaviyo Template' : 'Submit to Klaviyo'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={syncDialogOpen} onOpenChange={setSyncDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Klaviyo Sync Result</DialogTitle>
            <DialogDescription>Results from syncing email addresses with Klaviyo</DialogDescription>
          </DialogHeader>
          {syncResult && (
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <span className="text-slate-500">List ID</span>
                <span className="font-mono text-slate-800">{syncResult.list_id ?? '—'}</span>
                <span className="text-slate-500">Added</span>
                <span className="font-semibold text-green-700">{syncResult.added ?? 0}</span>
                <span className="text-slate-500">Skipped</span>
                <span className="font-semibold text-slate-700">{syncResult.skipped ?? 0}</span>
                <span className="text-slate-500">Failed</span>
                <span className="font-semibold text-red-600">{syncResult.failed ?? 0}</span>
              </div>
              {syncResult.failed_emails && syncResult.failed_emails.length > 0 && (
                <div className="mt-3">
                  <p className="text-sm font-medium text-slate-700 mb-1">Failed emails</p>
                  <div className="bg-slate-50 rounded-md border border-slate-200 p-3 max-h-40 overflow-y-auto">
                    {syncResult.failed_emails.map((email: string, i: number) => (
                      <p key={i} className="text-xs font-mono text-red-600">{email}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setSyncDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
